// Waiver content component for use inside useModal()

import { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { waiverGetTemplate, waiverSign } from '../utils/firebase';
import { ParentStatuses } from '../utils/enums';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import Spinner from './Spinner';
import Input from './Input';
import './WaiverContent.scss';

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const validationSchema = Yup.object({
  typedSignature: Yup.string().trim().required('Please type your full name'),
  agreed: Yup.boolean().oneOf([true], 'You must agree to continue'),
});

export default function WaiverContent({ playerName, playerId, onSigned }) {
  const { user, getParentStatus } = useFirebase();
  const { closeModal } = useModal();
  const parentStatus = getParentStatus(playerId, user.id);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [signed, setSigned] = useState(false);
  const [spinner, setSpinner] = useState(false);

  useEffect(() => {
    waiverGetTemplate({ type: 'liability' })
      .then((res) => setTemplate(res.data))
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load waiver');
        closeModal();
      })
      .finally(() => setLoading(false));
  }, [closeModal]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSpinner(true);
    try {
      const res = await waiverSign({
        playerId,
        waiverTemplateId: template.templateId,
        typedSignature: values.typedSignature.trim(),
      });
      setSigned(true);
      if (res.data?.emailSent) {
        toast.success('A copy of the waiver has been sent to your email');
      } else {
        toast.warn(
          "We couldn't email you a copy, but your waiver was signed successfully",
        );
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to sign waiver');
    } finally {
      setSpinner(false);
      setSubmitting(false);
    }
  };

  const successMessage =
    parentStatus === ParentStatuses.APPROVED
      ? 'Thank you! Your waiver is now active.'
      : 'Thank you! Your waiver will be activated after the coach confirms your parenting.';

  if (loading) return <Spinner show />;

  if (signed) {
    return (
      <div className='waiver-success'>
        <p>{successMessage}</p>
        <button
          className='waiver-btn'
          onClick={() => {
            onSigned(playerId);
            closeModal();
          }}
        >
          OK
        </button>
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className='waiver-container'>
      <Spinner show={spinner} />
      <div
        className='waiver-body'
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            template.bodyHtml
              .replaceAll('{{PARENT_NAME}}', escapeHtml(user.name || ''))
              .replaceAll('{{PLAYER_NAME}}', escapeHtml(playerName || ''))
              .replaceAll(
                '{{WAIVER_VERSION}}',
                escapeHtml(template.version || ''),
              ),
          ),
        }}
      />
      <p className='waiver-player'>
        Player: <strong>{playerName}</strong>
      </p>

      <Formik
        initialValues={{ typedSignature: '', agreed: false }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form className='waiver-form'>
            <Input
              name='typedSignature'
              placeholder='Your full name (signature)'
              value={values.typedSignature}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.typedSignature ? errors.typedSignature : ''}
            />
            <label className='waiver-checkbox'>
              <Field type='checkbox' name='agreed' />
              <span>
                I am the parent/legal guardian of {playerName} and I agree to
                the terms above
              </span>
            </label>
            {touched.agreed && errors.agreed && (
              <p className='waiver-error'>{errors.agreed}</p>
            )}
            <button
              type='submit'
              className='waiver-btn'
              disabled={
                !values.agreed || !values.typedSignature.trim() || isSubmitting
              }
            >
              {isSubmitting ? 'Signing…' : 'Sign Waiver'}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}
